import { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { deviceType, isMobile } from 'react-device-detect'
import { isEmpty, makeDeviceType } from '@open-tender/utils'
import {
  resetCheck,
  resetConfirmation,
  resetErrors,
  resetTip,
  selectBrand,
  selectCartTotal,
  selectCheckout,
  selectConfirmationOrder,
  selectContent,
  selectCustomer,
  selectMenuSlug,
  selectOrder,
  setConfirmationOrder,
  setDeviceType,
  setSubmitting,
  validateOrder,
  useAppDispatch,
  useAppSelector,
  resetOrder,
  resetGroupOrder,
} from '@open-tender/cloud'
import { FormError, Content, Main, Headline } from 'components'
import CheckoutCancelEdit from './CheckoutCancelEdit'
import CheckoutCustomer from './CheckoutCustomer'
import CheckoutGuest from './CheckoutGuest'
import CheckoutPickup from './CheckoutPickup'
import CheckoutDelivery from './CheckoutDelivery'
import CheckoutCart from './CheckoutCart'
import CheckoutHeader from './CheckoutHeader'
import CheckoutAddress from './CheckoutAddress'
import CheckoutDetails from './CheckoutDetails'
import CheckoutCurbside from './CheckoutCurbside'
import CheckoutSurcharges from './CheckoutSurcharges'
import CheckoutDiscounts from './CheckoutDiscounts'
import CheckoutPromoCodes from './CheckoutPromoCodes'
import CheckoutTip from './CheckoutTip'
import CheckoutGiftCards from './CheckoutGiftCards'
import CheckoutTenders from './CheckoutTenders'
import CheckoutSection from './CheckoutSection'
import CheckoutSubmit from './CheckoutSubmit'
import {
  makeAddressError,
  makeCustomerError,
  makeFormTitle,
} from './Checkout.utils'
import {
  CheckoutContent,
  CheckoutInfo,
  CheckoutSidebar,
  CheckoutSidebarContent,
  CheckoutTitle,
  CheckoutView,
} from './Checkout.styled'

const Checkout = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { title, has_promo_codes, has_gift_cards } =
    useAppSelector(selectBrand) || {}
  const { checkout: config } = useAppSelector(selectContent) || {}
  const cartTotal = useAppSelector(selectCartTotal)
  const menuSlug = useAppSelector(selectMenuSlug)
  const { serviceType, revenueCenter } = useAppSelector(selectOrder)
  const { revenue_center_id: revenueCenterId } = revenueCenter || {}
  const { auth } = useAppSelector(selectCustomer)
  const hasCustomer = auth ? true : false
  const { check, form, errors, submitting, completedOrder } =
    useAppSelector(selectCheckout)
  const confirmedOrder = useAppSelector(selectConfirmationOrder)
  const hasConfirmationOrder = confirmedOrder ? true : false
  const hasCheck = check ? true : false
  const hasFormCustomer = !isEmpty(form.customer) ? true : false
  const formError = errors ? (errors.form as string) || null : null
  const customerError = makeCustomerError(errors)
  const checkoutError = customerError || formError
  const addressError = makeAddressError(errors)
  const deviceTypeName = makeDeviceType(deviceType)
  const { formTitle, hasAddress } = makeFormTitle(check, serviceType)

  useEffect(() => {
    if (!submitting && formError) window.scrollTo(0, 0)
  }, [formError, submitting])

  useEffect(() => {
    dispatch(setSubmitting(false))
    dispatch(resetConfirmation())
    dispatch(setDeviceType(deviceTypeName))
    return () => {
      dispatch(resetErrors())
      dispatch(resetTip())
      dispatch(resetCheck())
    }
  }, [dispatch, deviceTypeName])

  useEffect(() => {
    if (!completedOrder && hasFormCustomer && !hasCheck) {
      dispatch(validateOrder())
    }
  }, [dispatch, completedOrder, hasFormCustomer, hasCheck])

  useEffect(() => {
    if (completedOrder) {
      if (!hasConfirmationOrder) {
        dispatch(setConfirmationOrder(completedOrder))
        ;(async () => {
          await dispatch(resetOrder())
          await dispatch(resetGroupOrder())
          navigate('/confirmation')
        })()
      }
    } else if (cartTotal === 0) {
      navigate(menuSlug)
    } else if (!revenueCenterId || !serviceType) {
      navigate('/account')
    } else if (!hasCustomer && !hasFormCustomer) {
      navigate('/checkout/guest')
    }
  }, [
    dispatch,
    navigate,
    cartTotal,
    menuSlug,
    revenueCenterId,
    serviceType,
    completedOrder,
    hasCustomer,
    hasFormCustomer,
    hasConfirmationOrder,
  ])

  return (
    <>
      <Helmet>
        <title>Checkout | {title}</title>
      </Helmet>
      <Content>
        <CheckoutHeader />
        <Main style={{ overflow: 'hidden' }}>
          <CheckoutView>
            <CheckoutContent>
              <CheckoutTitle>
                <Headline as="h1">{config?.title}</Headline>
                <p>{config?.subtitle}</p>
                <CheckoutCancelEdit />
                {checkoutError && <FormError errMsg={checkoutError} />}
                {addressError && <FormError errMsg={addressError} />}
              </CheckoutTitle>
              <CheckoutInfo>
                {auth ? <CheckoutCustomer /> : <CheckoutGuest />}
                {check && (
                  <>
                    {['WALKIN', 'PICKUP'].includes(serviceType ?? 'WALKIN') ? (
                      <CheckoutPickup />
                    ) : serviceType === 'DELIVERY' ? (
                      <CheckoutDelivery />
                    ) : null}
                  </>
                )}
              </CheckoutInfo>
              {formTitle && (
                <CheckoutSection title={formTitle} style={{ padding: '0' }}>
                  <CheckoutAddress />
                  <CheckoutDetails hasAddress={hasAddress} />
                </CheckoutSection>
              )}
              {check && (
                <>
                  <CheckoutCurbside
                    setInitialValue={formTitle ? false : true}
                  />
                  <CheckoutSurcharges />
                  <CheckoutDiscounts />
                  {has_promo_codes && <CheckoutPromoCodes />}
                  <CheckoutTip />
                  {isMobile && <CheckoutCart />}
                  {has_gift_cards && <CheckoutGiftCards />}
                  <CheckoutTenders />
                  <CheckoutSubmit />
                </>
              )}
            </CheckoutContent>
            <CheckoutSidebar>
              <CheckoutSidebarContent>
                {!isMobile && check && <CheckoutCart />}
              </CheckoutSidebarContent>
            </CheckoutSidebar>
          </CheckoutView>
        </Main>
      </Content>
    </>
  )
}

export default Checkout
